import { useId, type ReactNode } from 'react';
import { Card, CardBody, CardHeader, Flex, Text } from '@backstage/ui';

/**
 * The home page card shell on BUI, in place of core-components' InfoCard that
 * the upstream home cards used: a bold title over a divider, then the content.
 * `titleAddon` sits next to the title (a count) and `actions` at the right.
 * `flush` drops the divider and the body padding, for content such as tabs
 * that draws its own divider and spacing.
 */
export const HomeCard = ({
  title,
  titleAddon,
  actions,
  flush = false,
  children,
}: {
  title: string;
  titleAddon?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  children: ReactNode;
}) => {
  const titleId = useId();
  return (
    <Card role="region" aria-labelledby={titleId}>
      <CardHeader
        style={{
          padding: flush
            ? 'var(--bui-space-4) var(--bui-space-5) var(--bui-space-1)'
            : 'var(--bui-space-4) var(--bui-space-5)',
          borderBottom: flush ? undefined : '1px solid var(--bui-border-1)',
        }}
      >
        <Flex align="center" justify="between" gap="2">
          <Flex align="center" gap="2">
            <Text as="h2" variant="title-small" weight="bold" id={titleId}>
              {title}
            </Text>
            {titleAddon}
          </Flex>
          {actions}
        </Flex>
      </CardHeader>
      <CardBody style={{ padding: flush ? 0 : 'var(--bui-space-4)' }}>
        {children}
      </CardBody>
    </Card>
  );
};
